// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { appStore as store } from '@advisor-app/store/store';
import Swal from 'sweetalert2';
import { fetchEngineApi } from '@advisor-app/api/engine-api';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
  type ShowModalOptions,
} from '@shared/ui-kit/components/popups/show-modal';
import {
  getSavedPortfolios,
  resetSavedPortfolios,
} from '@advisor-app/store/slices/my-research';
import { type SavingHolding } from '@shared/data-access/interfaces/api/engine/citec';
import { type SavedPortfolio } from '@shared/data-access/interfaces/redux/my-research-state';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';

interface SavePortfolioProps {
  benchmark: string;
  savingHoldings?: SavingHolding[];
  onClose?: () => void;
  portfolios?: string[];
  t: TFunction;
}

export const savePortfolio = ({
  benchmark,
  savingHoldings,
  onClose,
  portfolios,
  t,
}: SavePortfolioProps) => {
  showModal({
    ...MODAL_TEMPLATES_OPTIONS(t).SAVE_PORTFOLIO,
    preConfirm: name => {
      /* istanbul ignore if */
      if (portfolios?.includes(name)) {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t(
            'research.portfolio-exists',
            "A portfolio with name '{{name}}' already exists",
            {
              ns: 'research',
              name,
            },
          ),
          content: t(
            'research.enter-different-name',
            'Please enter a different portfolio name.',
            {
              ns: 'research',
            },
          ),
          buttons: {
            confirm: { show: true, text: t('common.ok', '', { ns: 'common' }) },
          },
        } as ShowModalOptions);
        return false;
      }

      /* istanbul ignore if */
      if (!name) {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t(
            'research.unnamed-report-warning',
            "Can't save unnamed portfolio.",
            {
              ns: 'research',
            },
          ),
          content: t(
            'research.unnamed-report-message',
            'Please enter a valid portfolio name.',
            {
              ns: 'research',
            },
          ),
          buttons: {
            confirm: { show: true, text: t('common.ok', '', { ns: 'common' }) },
          },
        } as ShowModalOptions);

        return false;
      }

      const payload = {
        portfolio: name,
        benchmark,
        creation_date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        holdings: savingHoldings,
      };

      return fetchEngineApi<SavedPortfolio>({
        endpoint: 'citec/portfolio',
        method: 'POST',
        payload,
      }).then(
        res => res.data,
        error => error,
      );
    },
    allowOutsideClick: () => !Swal.isLoading(),
  } as ShowModalOptions).then(result => {
    /* istanbul ignore if */
    if (result.value instanceof Error)
      return showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
        title: t('errors.error-title', { ns: 'errors' }),
        content: t(
          'errors.unhandled-error',
          'An error has occurred while saving the portfolio',
          {
            ns: 'errors',
          },
        ),
        buttons: {
          confirm: { show: true, text: t('common.ok', '', { ns: 'common' }) },
        },
      } as ShowModalOptions);

    /* istanbul ignore if */
    if (result.isConfirmed) {
      store.dispatch(resetSavedPortfolios());
      store.dispatch(getSavedPortfolios());

      showModal({
        content: t('research.save-success', 'The portfolio is saved correctly', {
          ns: 'research',
        }),
        buttons: {
          confirm: { show: true, text: t('common.ok', '', { ns: 'common' }) },
        },
        allowOutsideClick: false,
      } as ShowModalOptions).then(() => onClose?.());
    }
  });
};
